import {ChangeDetectionStrategy, Component, computed, inject, input, resource, signal} from '@angular/core';
import {DatePipe, JsonPipe} from '@angular/common';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {ConfirmationService, MessageService, PrimeTemplate} from 'primeng/api';
import {TableModule} from 'primeng/table';
import {Tag} from 'primeng/tag';
import {Button} from 'primeng/button';
import {Dialog} from 'primeng/dialog';

import {LabeledComponent} from '../../shared/components/labeled/labeled.component';
import {CopyButtonComponent} from '../../shared/components/copy-button/copy-button.component';
import {SubmitButtonComponent} from '../../shared/components/submit-button/submit-button.component';
import {TypedTableRowDirective} from '../../shared/typed-table-row.directive';
import {
  applicationStatusToTag,
  failureCountToTag,
  homeyStatusToTag,
  lastSyncStatusToTag,
  loginStatusToTag,
  nycProfileStatusToTag,
  paymentStatusToTag,
  Severity,
  subscriptionStatusToTag,
  syncStatusToTag,
} from '../../shared/enum-to-tag';

import {UsersClient} from '../../api-client/api/api';
import {ActiveUserDetailDto} from '../../api-client/model/active-user-detail-dto';
import {DeletedUserDto} from '../../api-client/model/deleted-user-dto';
import {UserDetailDto} from '../../api-client/model/user-detail-dto';
import {IgnoredReason} from '../../api-client/model/ignored-reason';
import {ConnectedAccountProvider} from '../../api-client/model/connected-account-provider';

type SyncProcessingTag = {
  readonly label: string;
  readonly severity: Severity;
};

@Component({
  selector: 'homey-user-page',
  standalone: true,
  imports: [
    DatePipe,
    JsonPipe,
    PrimeTemplate,
    TableModule,
    Tag,
    Button,
    Dialog,
    LabeledComponent,
    CopyButtonComponent,
    SubmitButtonComponent,
    TypedTableRowDirective,
  ],
  templateUrl: './user-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserPageComponent {
  public readonly userId = input.required<string>();

  private readonly usersClient = inject(UsersClient);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly router = inject(Router);

  protected readonly user = resource<UserDetailDto, {id: string}>({
    params: () => ({id: this.userId()}),
    loader: ({params: {id}}) => firstValueFrom(this.usersClient.getUser(id)),
  });

  protected readonly activeUser = computed<ActiveUserDetailDto | undefined>(() => {
    const value = this.user.value();
    return value && value.type === 'user' ? value : undefined;
  });

  protected readonly deletedUser = computed<DeletedUserDto | undefined>(() => {
    const value = this.user.value();
    return value && value.type === 'deleted' ? value : undefined;
  });

  protected readonly subscriptionStatusToTag = subscriptionStatusToTag;
  protected readonly paymentStatusToTag = paymentStatusToTag;
  protected readonly nycProfileStatusToTag = nycProfileStatusToTag;
  protected readonly lastSyncStatusToTag = lastSyncStatusToTag;
  protected readonly loginStatusToTag = loginStatusToTag;
  protected readonly applicationStatusToTag = applicationStatusToTag;
  protected readonly homeyStatusToTag = homeyStatusToTag;
  protected readonly failureCountToTag = failureCountToTag;
  protected readonly syncStatusToTag = syncStatusToTag;

  protected readonly tableScrollHeight = '360px';

  protected readonly syncProcessingTag = computed<SyncProcessingTag | undefined>(() => {
    const state = this.activeUser()?.syncProcessingState;

    if (!state) {
      return undefined;
    }

    if (state.locked) {
      return {label: 'Syncing', severity: 'warn'};
    }

    if (state.priorityQueued) {
      return {label: 'Prio sync scheduled', severity: 'info'};
    }

    if (state.scheduledQueued) {
      return {label: 'Low prio sync scheduled', severity: 'info'};
    }

    return undefined;
  });

  // The Register-in-NYC dialog shows the Housing Connect credentials that are already part of
  // the loaded user, so opening it is purely local state -- no second request, and no separate
  // endpoint that returns credentials.
  protected readonly registerInNycVisible = signal(false);

  protected readonly nycAuth = computed(() => {
    const profile = this.activeUser()?.housingConnectProfile;
    return this.registerInNycVisible() && profile ? profile : undefined;
  });

  protected readonly registerInNycDisabled = computed(
    () => this.activeUser()?.housingConnectProfile?.status === 'complete'
  );

  protected readonly unstuckDisabled = computed(
    () => this.activeUser()?.housingConnectProfile?.status !== 'waiting-on-email2-verification'
  );

  protected readonly registerInNyc = async (): Promise<void> => {
    this.registerInNycVisible.set(true);
  };

  protected closeRegisterInNycDialog(): void {
    this.registerInNycVisible.set(false);
  }

  protected readonly confirmRegisteredInNyc = async (): Promise<void> => {
    const user = this.activeUser();
    if (!user) {
      return;
    }

    await firstValueFrom(this.usersClient.markUserAsRegisteredInNyc(user.id));
    this.registerInNycVisible.set(false);
    this.user.reload();
    this.messageService.add({
      severity: 'success',
      summary: 'Registered in NYC',
      detail: `User ${user.email} marked as registered in NYC`,
    });
  };

  protected readonly unstuck = async (): Promise<void> => {
    const user = this.activeUser();
    if (!user) {
      return;
    }

    await firstValueFrom(this.usersClient.unstuckUser(user.id));
    this.user.reload();
    this.messageService.add({
      severity: 'success',
      summary: 'User unstuck',
      detail: `User ${user.email} unstuck from waiting on NYC email 2 verification`,
    });
  };

  protected goToSendTextMessage(): void {
    this.router.navigate(['/users', this.userId(), 'send-text-message']);
  }

  protected confirmDelete(): void {
    const user = this.activeUser();
    if (!user) {
      return;
    }

    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this user? This action cannot be undone.',
      header: 'Delete user',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.usersClient.deleteUser(user.id).subscribe(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'User deleted',
            detail: `User ${user.email} deleted`,
          });
          this.router.navigate(['/users']);
        });
      },
    });
  }

  protected ignoredReasonLabel(reason: IgnoredReason): string {
    switch (reason) {
      case 'prevent-sync':
        return 'Prevent sync';
      case 'incorrect-username-password':
        return 'Incorrect username/password';
      case 'unknown':
      default:
        return 'Unknown';
    }
  }

  protected ignoredReasonSeverity(reason: IgnoredReason): Severity {
    switch (reason) {
      case 'prevent-sync':
        return 'warn';
      case 'incorrect-username-password':
        return 'danger';
      case 'unknown':
      default:
        return 'info';
    }
  }

  protected connectedAccountSeverity(provider: ConnectedAccountProvider): Severity {
    return provider === 'cognito' ? 'success' : 'info';
  }

  protected connectedAccountProviderLabel(provider: ConnectedAccountProvider): string {
    switch (provider) {
      case 'google':
        return 'Google (federated)';
      case 'apple':
        return 'Apple (federated)';
      default:
        return 'Homey login';
    }
  }

}
